import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../context/ToastContext'
import BusinessForm from './BusinessForm'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import Modal from '../../components/ui/Modal'

function BusinessSettings({ business, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()
  const toast = useToast()

  const handleUpdate = async (formData) => {
    setLoading(true)
    const { error } = await onUpdate(formData)
    setLoading(false)

    if (error) {
      toast.error('Failed to update business')
      return
    }

    toast.success('Business updated')
    setIsEditing(false)
  }

  const handleDelete = async () => {
    setLoading(true)
    const { error } = await onDelete()
    setLoading(false)

    if (error) {
      toast.error('Failed to delete business')
      return
    }

    toast.success('Business deleted')
    navigate('/dashboard')
  }

  if (isEditing) {
    return (
      <Card>
        <h3 className="card-title mb-4">Edit Business</h3>
        <BusinessForm
          initialData={business}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
          loading={loading}
        />
      </Card>
    )
  }

  return (
    <>
      <div className="grid grid-2 gap-6">
        <Card>
          <h3 className="card-title mb-4">Business Information</h3>
          <dl className="flex flex-col gap-3">
            <div>
              <dt className="text-sm text-muted">Name</dt>
              <dd className="font-medium">{business.name}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted">Description</dt>
              <dd>{business.description || '—'}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted">Status</dt>
              <dd className="capitalize">{business.status}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted">Website</dt>
              <dd>
                {business.website ? (
                  <a href={business.website} target="_blank" rel="noopener noreferrer">
                    {business.website}
                  </a>
                ) : '—'}
              </dd>
            </div>
          </dl>
          <Button
            variant="secondary"
            className="mt-4"
            onClick={() => setIsEditing(true)}
          >
            Edit Business
          </Button>
        </Card>

        <Card>
          <h3 className="card-title mb-4" style={{ color: 'var(--error)' }}>
            Danger Zone
          </h3>
          <p className="text-muted mb-4">
            Deleting a business will permanently remove all associated transactions and data.
            This action cannot be undone.
          </p>
          <Button variant="danger" onClick={() => setIsDeleting(true)}>
            Delete Business
          </Button>
        </Card>
      </div>

      <Modal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        title="Delete Business"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleting(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={loading}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{business.name}</strong>?
        </p>
        <p className="text-muted mt-2">
          This will permanently delete all transactions and data associated with this business.
        </p>
      </Modal>
    </>
  )
}

export default BusinessSettings
